import React, { useState, useEffect } from 'react';
import { useAppContext } from '../AppContext';
import * as LucideIcons from 'lucide-react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions'; // Import httpsCallable
import SyndicateInfoModal from './SyndicateInfoModal';
import { useTranslation } from 'react-i18next';

const LotterySyndicate = () => {
    const { db, userId, appFunctions, currentUserProfile, setMessage, showConfirmation } = useAppContext();
    const { t } = useTranslation();
    const [syndicates, setSyndicates] = useState([]);
    const [mySyndicate, setMySyndicate] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isCreating, setIsCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [showInfoModal, setShowInfoModal] = useState(false);

    const activeSyndicateId = currentUserProfile?.activeSyndicateId;

    useEffect(() => {
        const q = query(collection(db, `artifacts/whispers-of-harmony/public/data/lottery_syndicates`));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const allSyndicates = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSyndicates(allSyndicates);
            if (activeSyndicateId) {
                setMySyndicate(allSyndicates.find(s => s.id === activeSyndicateId));
            }
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [db, activeSyndicateId]);

    const handleCreate = async () => {
        if (newName.length < 3) {
            setMessage(t('lotterySyndicate.nameTooShort'), 'error');
            return;
        }
        setIsCreating(true);
        try {
            const createSyndicate = httpsCallable(appFunctions, 'createSyndicate');
            await createSyndicate({ name: newName });
            setNewName('');
            setMessage(t('lotterySyndicate.createSuccess'), 'success');
        } catch (err) {
            console.error(err);
            setMessage(err.message, 'error');
        } finally {
            setIsCreating(false);
        }
    };

    const handleJoin = async (syndicateId) => {
        try {
            const joinSyndicate = httpsCallable(appFunctions, 'joinSyndicate');
            await joinSyndicate({ syndicateId });
            setMessage(t('lotterySyndicate.joinSuccess'), 'success');
        } catch (err) {
            console.error(err);
            setMessage(err.message, 'error');
        }
    };

    const handleLeave = async () => {
        showConfirmation({
            message: t('lotterySyndicate.leaveConfirmation', { name: mySyndicate.name }),
            onConfirm: async () => {
                try {
                    const leaveSyndicate = httpsCallable(appFunctions, 'leaveSyndicate');
                    await leaveSyndicate();
                    setMySyndicate(null);
                    setMessage(t('lotterySyndicate.leaveSuccess'), 'success');
                } catch (err) {
                    console.error(err);
                    setMessage(err.message, 'error');
                }
            }
        });
    };

    if (isLoading) return <div>{t('lotterySyndicate.loading')}</div>;

    return (
        <div className="mt-8">
            {showInfoModal && <SyndicateInfoModal onClose={() => setShowInfoModal(false)} />}
            <div className="flex items-center gap-2 mb-4">
                <h3 className="font-bold text-xl text-cyan-300">{t('lotterySyndicate.title')}</h3>
                <button onClick={() => setShowInfoModal(true)} className="text-cyan-300 hover:text-cyan-100">
                    <LucideIcons.Info size={18} />
                </button>
            </div>
            {mySyndicate ? (
                <div className="bg-gray-900/50 p-4 rounded-xl border border-cyan-500/30">
                    <h4 className="font-bold text-lg">{t('lotterySyndicate.mySyndicate', { name: mySyndicate.name })}</h4>
                    <div className="my-2">
                        <p className="text-sm text-gray-400">{t('lotterySyndicate.members', { count: mySyndicate.members.length })}</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                            {mySyndicate.members.map(m => (
                                <div key={m.userId} className="flex items-center bg-gray-800/70 rounded-full p-1 pr-2 text-xs">
                                    <img src={m.photoURL} alt={m.displayName} className="w-5 h-5 rounded-full mr-2"/>
                                    <span>{m.displayName}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <button onClick={handleLeave} className="small-action-button bg-red-600 hover:bg-red-700 text-white mt-2">{t('lotterySyndicate.leave')}</button>
                </div>
            ) : (
                <div className="bg-gray-900/50 p-4 rounded-xl border border-gray-700/30">
                    <h4 className="font-bold text-lg mb-2">{t('lotterySyndicate.createTitle')}</h4>
                    <div className="flex gap-2">
                        <input 
                            type="text"
                            value={newName}
                            onChange={e => setNewName(e.target.value)}
                            placeholder={t('lotterySyndicate.placeholder')}
                            className="w-full bg-gray-800 border border-gray-700 rounded-lg p-2 text-white"
                        />
                        <button onClick={handleCreate} disabled={isCreating} className="small-action-button bg-green-600 hover:bg-green-700 text-white">
                            {isCreating ? t('lotterySyndicate.creating') : t('lotterySyndicate.create')}
                        </button>
                    </div>
                </div>
            )}

            <div className="mt-6">
                <h4 className="font-bold text-lg mb-2">{t('lotterySyndicate.joinTitle')}</h4>
                <div className="space-y-3 max-h-60 overflow-y-auto custom-scrollbar">
                    {syndicates.filter(s => !s.members.some(m => m.userId === userId)).length > 0 ? (
                        syndicates.filter(s => !s.members.some(m => m.userId === userId)).map(syndicate => (
                            <div key={syndicate.id} className="bg-gray-800/60 p-3 rounded-lg flex justify-between items-center">
                                <div>
                                    <p className="font-semibold text-white">{syndicate.name}</p>
                                    <div className="flex items-center mt-1">
                                        {syndicate.members.slice(0, 5).map((member, index) => (
                                            <img key={member.userId} src={member.photoURL} alt={member.displayName} className="w-6 h-6 rounded-full border-2 border-gray-900" style={{ marginLeft: index > 0 ? '-8px' : 0 }} />
                                        ))}
                                        <span className="text-xs text-gray-400 ml-2">{t('lotterySyndicate.memberCount', { count: syndicate.members.length })}</span>
                                    </div>
                                    {syndicate.totalWinnings > 0 && <p className="text-xs text-yellow-400 mt-1">{t('lotterySyndicate.totalWinnings', { amount: syndicate.totalWinnings.toLocaleString() })}</p>}
                                </div>
                                <button onClick={() => handleJoin(syndicate.id)} className="small-action-button bg-blue-600 hover:bg-blue-700 text-white">{t('lotterySyndicate.join')}</button>
                            </div>
                        ))
                    ) : (
                        <div className="text-center text-gray-500 p-8">
                            <LucideIcons.Users size={32} className="mx-auto mb-2" />
                            <h5 className="font-bold">{t('lotterySyndicate.noSyndicates')}</h5>
                            <p className="text-sm">{t('lotterySyndicate.noSyndicatesDescription')}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LotterySyndicate;
